import {
  COUNTRY_CODES,
  CountryCode,
  countryCallingCodes,
} from '../models/app.models';
import {
  IApplicationClassPrice,
  IClass,
  ICountry,
  ICurrency,
  IMedia,
  IMetaComponent,
  IOrder,
  IOrderItemClass,
  IStrapiCollectionResult,
  ITrademarkRegistrationCountryLanding,
  ITrademarkSearchCountryLanding,
  OrderStatus,
  ServiceType,
  TrackerStatus,
} from '../models/strapi.models';
import { logError } from '../services/log.service';
import {
  AppOrder,
  IAppOrder,
  IAppOrderClass,
} from '../store/models/store.models';
import { IAppPage, IAppPageLocalization } from '../store/slices/appSlice';

export const getCurrencySymbol = (currencyCode?: string) => {
  let currencySymbol = 'US$';

  if (currencyCode === 'EUR') {
    currencySymbol = '€';
  }

  if (currencyCode === 'GBP') {
    currencySymbol = '£';
  }

  return currencySymbol;
};

export const addCurrencySymbol = (
  value: number | undefined,
  currencyCode: string,
  locale: string
) => {
  const currencySymbol = getCurrencySymbol(currencyCode);

  if (locale === 'en') {
    return `${currencySymbol} ${value || '-'}`;
  } else {
    return `${value || '-'} ${currencySymbol}`;
  }
};

export const applyCurrencyConversionRate = (
  value: number,
  conversionRate: number | undefined
) => {
  const convertedValue = value * (conversionRate || 1);
  return Math.ceil(convertedValue / 5) * 5;
};

export const convertValueToUSD = (
  value: number,
  sourceCurrency: ICurrency | undefined
) => {
  if (!sourceCurrency || sourceCurrency.isoCode === 'USD') {
    return value;
  }
  return Math.ceil(value / sourceCurrency.conversionRate! / 5) * 5;
};

export const formatDate = (
  date: string | Date = new Date(),
  isISO: boolean = false
) => {
  const newDate = new Date(date);
  const day = String(newDate.getDate()).padStart(2, '0');
  const month = String(newDate.getMonth() + 1).padStart(2, '0'); // Months are zero-based
  const year = newDate.getFullYear();

  if (isISO) {
    return `${year}-${month}-${day}`;
  }

  return `${day}/${month}/${year}`;
};

export const addDays = (date: Date, days: number) => {
  const newDate = new Date(Number(date));
  newDate.setDate(date.getDate() + days);
  return newDate;
};

export const flatStrapiCollection = <T>(
  result: IStrapiCollectionResult<T>
): T[] => {
  if (!result || !result.data || !Array.isArray(result.data)) {
    return [];
  }

  const flattened: T[] = [];
  result.data.forEach((x) => {
    flattened.push({
      id: x.id,
      ...x.attributes,
    });
  });

  return flattened as T[];
};

export const flatStrapiObject = <T>(result: any): T => {
  if (!result || !result.data || !result.data.id || !result.data.attributes) {
    return {} as T;
  }

  let obj: any = {
    id: result.data.id,
    ...result.data.attributes,
  };
  return obj as T;
};

export const getTranslation = <T>(
  componentCollection: T[] | undefined | null,
  locale: string
): T => {
  if (!componentCollection) {
    return { text: 'n/a' } as T;
  }

  try {
    const translation = componentCollection.find(
      (x) => ((x as any)['language'] as string).trim() === locale
    ) as T;
    const fallbackTranslation = componentCollection.find(
      (x) =>
        ((x as any)['language'] as string).trim() === 'en' ||
        ((x as any)['language'] as string).trim() === 'es' ||
        ((x as any)['language'] as string).trim() === 'fr'
    ) as T;
    return translation || fallbackTranslation || ({ text: 'n/a' } as T);
  } catch (error) {
    return { text: 'n/a' } as T;
  }
};

export const randomNumber = (min: number, max: number): number => {
  return Math.floor(Math.random() * (max - min + 1) + min);
};

export const downloadFile = async (
  url: string,
  fileName: string,
  throwOnError = false
): Promise<void> => {
  try {
    const response = await fetch(url);
    if (response.status !== 200) {
      if (throwOnError) throw new Error('File download failed');
      return;
    }

    const blob = await response.blob();
    const downloadLink = document.createElement('a');
    downloadLink.href = URL.createObjectURL(blob);
    downloadLink.download = fileName;
    document.body.appendChild(downloadLink);
    downloadLink.click();

    setTimeout(() => {
      URL.revokeObjectURL(downloadLink.href);
      document.body.removeChild(downloadLink);
    }, 100);
  } catch (error) {
    if (throwOnError) throw error;
    logError([error], 'Utils.downloadFile');
  }
};

export const getOrderStatusPropertyName = (status: OrderStatus): string => {
  if (status === OrderStatus.CANCELED) {
    return 'orderCanceled';
  }

  if (status === OrderStatus.COMPLETED) {
    return 'orderCompleted';
  }

  if (status === OrderStatus.DRAFT) {
    return 'orderDraft';
  }

  if (status === OrderStatus.PENDING) {
    return 'orderPending';
  }

  if (status === OrderStatus.PAID_BUTTON_ERROR_CREATING_TRACKERS) {
    return 'orderButErrorCreatingTrackers';
  }

  if (status === OrderStatus.PAID_AND_PENDING_TRACKER_CREATION) {
    return 'orderPaidAndPendingTrackersCreation';
  }

  return '';
};

export const getPaymentStatusPropertyName = (
  status: 'completed' | 'pending'
): string => {
  if (status === 'pending') {
    return 'paymentPending';
  }

  if (status === 'completed') {
    return 'paymentDone';
  }

  return '';
};

export const getServiceTrackerOrderStatusPropertyName = (
  status: TrackerStatus
): string => {
  if (status === TrackerStatus.IN_PROCESS) {
    return 'trackerInProcess';
  }

  if (status === TrackerStatus.REGISTERED) {
    return 'trackerRegistered';
  }

  if (status === TrackerStatus.FILED) {
    return 'trackerFiled';
  }

  if (status === TrackerStatus.COMPLETED) {
    return 'trackerCompleted';
  }

  if (status === TrackerStatus.INACTIVE) {
    return 'trackerInactive';
  }

  if (status === TrackerStatus.ABANDONED) {
    return 'trackerAbandoned';
  }

  if (status === TrackerStatus.REFUSED) {
    return 'trackerRefused';
  }

  if (status === TrackerStatus.WAITIN_FOR_POA) {
    return 'trackerWaitingForPoa';
  }
  return '';
};

export const mapAppOrder = (
  order: IOrder,
  userId: number,
  locale?: string
): IAppOrder => {
  const appOrder: IAppOrder = new AppOrder(ServiceType.SEARCH); // Set initial default order
  appOrder.userId = userId || 0;
  appOrder.strapiOrderId = order.id;
  appOrder.currency = order.currency as ICurrency;
  appOrder.totalPrice = order.totalPrice || 0;

  if (order.orderItems) {
    appOrder.type = order.orderItems[0]?.serviceType;
    appOrder.trademarkName = order.orderItems[0]?.trademarkText || '-';
    appOrder.prices = order.orderItems[0]?.prices || [];

    if (order.orderItems[0]?.classes) {
      appOrder.trademarkClasses = (
        order.orderItems[0]?.classes as IOrderItemClass[]
      ).map((x: IOrderItemClass) => ({
        id: x.class.id as number,
        code: (x.class as IClass).code,
        name: getTranslation(
          (x.class as IClass).descriptionTranslations,
          locale || 'en'
        ).text as string,
        description: x.description,
      }));
    }

    if (order.orderItems[0]?.countries) {
      appOrder.trademarkCountries = (
        order.orderItems[0]?.countries as ICountry[]
      ).map((x: ICountry) => ({
        name: getTranslation(x.nameTranslations, locale || 'en').text as string,
        code: x.iso,
        dividesByClass: x.dividesByClass,
        id: x.id as number,
        applicationClassPrices: x.applicationClassPrices.map((f) => {
          f.valueName = f.valueName || '';
          return f;
        }),
        finalFees: x.finalFees.map((f) => {
          f.valueName = f.valueName || '';
          return f;
        }),
        searchClassPrices: x.searchClassPrices.map((f) => {
          f.valueName = f.valueName || '';
          return f;
        }),
      }));
    }

    if (order.orderItems[0]?.logo) {
      const thumbnailUrl = (order.orderItems[0].logo as IMedia)?.formats
        ?.thumbnail?.url;
      if (!!thumbnailUrl) {
        appOrder.trademarkLogo = thumbnailUrl;
      }
    }
  }

  return appOrder;
};

export const getFlagPath = (countryCode: string) => {
  return `/assets/images/common/flags/${countryCode.toLowerCase()}.svg`;
};

export const textToSlug = (text: string) => {
  return text
    .toLowerCase() // Convertir todo a minúsculas
    .normalize('NFD') // Normalizar caracteres acentuados
    .replace(/[\u0300-\u036f]/g, '') // Eliminar caracteres diacríticos
    .replace(/[^\w-]+/g, '-') // Reemplazar caracteres especiales con guiones
    .replace(/--+/g, '-') // Reemplazar múltiples guiones con uno solo
    .replace(/^-+|-+$/g, ''); // Eliminar guiones al principio y al final
};

export const getCanonicalLink = (locale: string, slug: string | null) => {
  const host = process.env.NEXT_PUBLIC_WEBSITE_URL as string;
  if (slug) {
    slug = slug.replace(/^\/+/, '');
  }

  if (locale === 'en') {
    return !!slug ? `${host}/${slug}` : `${host}`;
  } else {
    return !!slug ? `${host}/${locale}/${slug}` : `${host}/${locale}`;
  }
};

export const getContentLanguageCode = (locale: string) => {
  if (locale === 'es') {
    return 'es-es';
  }
  if (locale === 'es') {
    return 'fr-fr';
  }

  return 'en-us';
};

export const getAlternateLinks = (path: string, appPages?: IAppPage[]) => {
  const host = process.env.NEXT_PUBLIC_WEBSITE_URL as string;
  const locales = JSON.parse(
    process.env.NEXT_PUBLIC_APP_LOCALES || '[]'
  ) as string[];
  const alternateLinks: { href: string; hrefLang: string }[] = [];

  if (!path || path === '/') {
    locales?.forEach((x) => {
      const href = x === 'en' ? `${host}` : `${host}/${x}`;
      alternateLinks.push({ href, hrefLang: x });

      if (x === 'en') {
        alternateLinks.push({ href: `${href}`, hrefLang: 'x-default' });
      }
    });
  } else {
    appPages
      ?.filter((x) => x.original === path)
      .forEach((x) => {
        alternateLinks.push({
          href: `${host}${x.source}`,
          hrefLang: x.language,
        });

        if (x.language === 'en') {
          alternateLinks.push({
            href: `${host}${x.source}`,
            hrefLang: 'x-default',
          });
        }
      });
  }

  return alternateLinks;
};

export const getAlternateLinksForBlogPost = (
  locale: string,
  slug: string,
  appPages?: IAppPage[]
) => {
  const host = process.env.NEXT_PUBLIC_WEBSITE_URL as string;
  const alternateLinks: { href: string; hrefLang: string }[] = [];

  if (locale !== 'en') {
    let localizations: IAppPageLocalization[] = [];
    appPages
      ?.filter((x) => !!x.localizations?.find((l) => l.id === parseInt(slug)))
      .forEach((p) => {
        if (p.localizations) {
          p.localizations.forEach((localization) => {
            if (!localizations.some((l) => l.id === localization.id)) {
              localizations.push(localization);
            }
          });
        }
      });

    localizations.forEach((l) => {
      appPages
        ?.filter((x) => x.original === `/blog/${l.id}`)
        .forEach((p) => {
          if (p.language === 'en') {
            alternateLinks.push({
              href: `${host}${p.source}`,
              hrefLang: 'x-default',
            });
          }

          alternateLinks.push({
            href: `${host}${p.source}`,
            hrefLang: p.language,
          });
        });
    });
  } else {
    appPages
      ?.filter((x) => x.original === `/blog/${slug}`)
      .forEach((p) => {
        if (p.language === 'en') {
          alternateLinks.push({
            href: `${host}${p.source}`,
            hrefLang: 'x-default',
          });
        }

        alternateLinks.push({
          href: `${host}${p.source}`,
          hrefLang: p.language,
        });
      });
  }

  // CRITICAL SEO FIX: Ensure self-referencing hreflang is always present
  const hasSelfReference = alternateLinks.some(
    (link) => link.hrefLang === locale
  );

  if (!hasSelfReference) {
    // Find the current page URL in appPages using postId (consistent with Layout.tsx language switcher)
    const currentPage = appPages?.find(
      (x) => x.postId === parseInt(slug) && x.language === locale
    );

    if (currentPage) {
      // Add self-reference from appPages data
      alternateLinks.push({
        href: `${host}${currentPage.source}`,
        hrefLang: locale,
      });
    } else {
      // Fallback: construct URL manually if not found in appPages
      const selfUrl =
        locale === 'en'
          ? `${host}/blog/${slug}`
          : `${host}/${locale}/blog/${slug}`;
      alternateLinks.push({ href: selfUrl, hrefLang: locale });
    }
  }

  // Ensure x-default is always present (should be English version)
  const hasXDefault = alternateLinks.some(
    (link) => link.hrefLang === 'x-default'
  );

  if (!hasXDefault) {
    const englishPage = appPages?.find(
      (x) => x.language === 'en' && x.original?.includes('/blog/')
    );

    if (englishPage) {
      alternateLinks.push({
        href: `${host}${englishPage.source}`,
        hrefLang: 'x-default',
      });
    } else if (locale === 'en') {
      // Current page is English, use it as x-default
      const currentPageUrl =
        alternateLinks.find((link) => link.hrefLang === 'en')?.href ||
        `${host}/blog/${slug}`;
      alternateLinks.push({ href: currentPageUrl, hrefLang: 'x-default' });
    }
  }

  return alternateLinks;
};

export const getAlternateLinksForCountry = (
  locale: string,
  slug: string,
  appPages?: IAppPage[]
) => {
  const host = process.env.NEXT_PUBLIC_WEBSITE_URL as string;
  const alternateLinks: { href: string; hrefLang: string }[] = [];
  const originalPath =
    appPages?.find((x) => x.destination.includes(slug))?.original || undefined;

  // Get all language versions including current locale (removed !== locale filter)
  appPages
    ?.filter((x) => x.original === originalPath)
    .forEach((p) => {
      alternateLinks.push({ href: `${host}${p.source}`, hrefLang: p.language });

      // Add x-default for English version
      if (p.language === 'en') {
        alternateLinks.push({
          href: `${host}${p.source}`,
          hrefLang: 'x-default',
        });
      }
    });

  // CRITICAL SEO FIX: Ensure self-referencing hreflang is always present
  const hasSelfReference = alternateLinks.some(
    (link) => link.hrefLang === locale
  );

  if (!hasSelfReference && originalPath) {
    // Find the current page URL in appPages
    const currentPage = appPages?.find(
      (x) => x.original === originalPath && x.language === locale
    );

    if (currentPage) {
      alternateLinks.push({
        href: `${host}${currentPage.source}`,
        hrefLang: locale,
      });
    }
  }

  return alternateLinks;
};

export const getImageSrcWithFallback = (
  media: IMedia | null | undefined,
  format: 'medium' | 'large' | 'xlarge' | 'full'
): string => {
  if (!!media) {
    if (format === 'medium' && !!media.formats && !!media.formats.medium) {
      return media.formats.medium.url as string;
    }

    if (format === 'large' && !!media.formats && !!media.formats.large) {
      return media.formats.large.url as string;
    }

    if (format === 'xlarge' && !!media.formats && !!media.formats.xlarge) {
      return media.formats.xlarge.url as string;
    }

    if (format === 'full' && !!media.url) {
      return media.url as string;
    }

    if (
      !!media.formats &&
      !media.formats.large &&
      !media.formats.medium &&
      !!media.formats.small
    ) {
      return media.formats.small.url as string;
    }

    return (media.formats?.thumbnail?.url || media.url) as string;
  }

  return '/assets/images/common/trademark-logo-fallback.svg';
};

export const getThumbnailSrcWithFallback = (media: IMedia): string => {
  if (!!media) {
    if (!!media.formats) {
      if (!!media.formats.thumbnail) {
        return media.formats.thumbnail.url as string;
      }
      if (!!media.formats.small) {
        return media.formats.small.url as string;
      }
    } else {
      return media.url as string;
    }
  }

  return '/assets/images/common/trademark-logo-fallback.svg';
};

export const getLocalizedSlugByCountryIso = (
  landings:
    | ITrademarkRegistrationCountryLanding[]
    | ITrademarkSearchCountryLanding[]
    | {
        country: { iso: string };
        metas: IMetaComponent[];
      }[],
  iso: string,
  locale: string
): string => {
  const landing = landings.find((x) => x.country.iso === iso);

  if (!landing) {
    return iso;
  }

  const metaData = (landing as any).metas || (landing as any).meta;
  const landingMeta = getTranslation<IMetaComponent>(metaData, locale);

  return (landingMeta as IMetaComponent)?.slug || iso;
};

export const getClassByCode = (
  classes: IAppOrderClass[],
  classCode: number
) => {
  return classes.find((x) => x.code === classCode);
};

export const htmlToText = (html: string) => {
  if (typeof document !== 'undefined') {
    var temp = document.createElement('DIV');
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || '';
  } else {
    return '';
  }
};

export const generateRandomId = () => {
  return Math.random().toString(36).slice(2, 12);
};

export const isValidPhoneNumber = (phoneNumber: string) => {
  const regex = /^\+?\d{7,15}$/;
  return regex.test(phoneNumber);
};

export const getCountryCodes = () => {
  return COUNTRY_CODES;
};

export const getCountryCallingCode = (
  countryCode: keyof typeof countryCallingCodes | CountryCode
) => {
  return countryCallingCodes[countryCode as keyof typeof countryCallingCodes];
};

export const decomposePhoneNumber = (
  fullPhoneNumber: string
): {
  dialCode: string;
  phoneNumber: string;
  countryCode: CountryCode | null;
} => {
  // Elimina cualquier carácter no numérico
  const numericPhoneNumber = fullPhoneNumber.replace(/\D/g, '');

  // Busca el código de marcación en el número de teléfono
  for (const countryCode of COUNTRY_CODES) {
    const dialCode = getCountryCallingCode(
      countryCode as keyof typeof countryCallingCodes
    );
    if (numericPhoneNumber.startsWith(dialCode)) {
      return {
        dialCode: `+${dialCode}`,
        phoneNumber: numericPhoneNumber.substring(dialCode.length),
        countryCode: countryCode as CountryCode,
      };
    }
  }

  // Si no se encuentra ningún código de marcación, devuelve el número de teléfono tal cual
  return {
    dialCode: '',
    phoneNumber: numericPhoneNumber,
    countryCode: null,
  };
};

export const removeObjectProperties = <T>(obj: T, propsToRemove: string[]) => {
  for (let prop in obj) {
    if (typeof obj[prop] === 'object' && obj[prop] !== null) {
      removeObjectProperties(obj[prop], propsToRemove);
    }
    if (propsToRemove.includes(prop)) {
      delete obj[prop];
    }
  }
  return obj;
};

export const keepObjectProperties = <T>(obj: any, propsToKeep: string[]): T => {
  let filteredObj: any = {};
  for (let prop in obj) {
    if (propsToKeep.includes(prop)) {
      filteredObj[prop] =
        typeof obj[prop] === 'object' && obj[prop] !== null
          ? keepObjectProperties(obj[prop], propsToKeep)
          : obj[prop];
    }
  }
  return filteredObj;
};

export const calculateStripeAmount = (amount: number, currency?: ICurrency) => {
  let stripeAmount = amount * 100;

  if (currency?.numberOfDecimals === 0) {
    stripeAmount = amount;
  }

  return stripeAmount;
};

export const exportDataToCSV = (data: any[], filename: string) => {
  const escapeCSVValue = (value: any): string => {
    if (value === null || value === undefined) {
      return '';
    }

    const stringValue = String(value);

    // If contains commas, quotes, or line breaks, it needs to be escaped
    if (
      stringValue.includes(',') ||
      stringValue.includes('"') ||
      stringValue.includes('\n') ||
      stringValue.includes('\r')
    ) {
      // Duplicate internal quotes and wrap in quotes
      return `"${stringValue.replace(/"/g, '""')}"`;
    }

    return stringValue;
  };

  const values = data.map((item) => Object.values(item).map(escapeCSVValue));

  if (data.length > 0) {
    values.unshift(Object.keys(data[0]).map(escapeCSVValue));
  }

  const csvContent =
    '\uFEFFsep=,\r\n' + values.map((row) => row.join(',')).join('\r\n');

  // Create a Blob instead of using data URI
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.setAttribute('href', URL.createObjectURL(blob));
  link.setAttribute('download', filename);
  document.body.appendChild(link);

  link.click();

  // Clean up
  setTimeout(() => {
    URL.revokeObjectURL(link.href);
    document.body.removeChild(link);
  }, 100);
};

// Convert Markdown contento to plain text by removing all Markdown and HTML tags
export const removeMarkDown = (text: string): string => {
  if (!text) return '';

  let plainText = text;

  // Quitar imágenes ![alt](url)
  plainText = plainText.replace(/!\[.*?\]\(.*?\)/g, '');

  // Quitar enlaces [texto](url)
  plainText = plainText.replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1');

  // Quitar títulos ##, ###, ####, etc.
  plainText = plainText.replace(/^#{1,6}\s*/gm, '');

  // Quitar listas -, *, +, y numeradas
  plainText = plainText.replace(/^\s*([-*+]|\d+\.)\s+/gm, '');

  // Quitar blockquotes >
  plainText = plainText.replace(/^\s*>\s?/gm, '');

  // Quitar código en bloque ```
  plainText = plainText.replace(/```[\s\S]*?```/g, '');

  // Quitar código en línea `
  plainText = plainText.replace(/`+/g, '');

  // Quitar formato de negrita, cursiva, tachado, subrayado
  plainText = plainText.replace(/[*_~]/g, '');

  // Quitar HTML tags
  plainText = plainText.replace(/<\/?[^>]+(>|$)/g, '');

  // Reemplazar saltos de línea por espacio
  plainText = plainText.replace(/[\r\n]+/g, ' ');

  // Reemplazar múltiples espacios por uno solo
  plainText = plainText.replace(/\s+/g, ' ').trim();

  return plainText;
};

export const splitTextAtWordBoundary = (
  text: string,
  maxLength: number
): { title: string; content: string } => {
  if (text.length <= maxLength) {
    return { title: text, content: '' };
  }

  let cutIndex = maxLength;
  while (cutIndex > 0 && text[cutIndex] !== ' ') {
    cutIndex--;
  }

  if (cutIndex === 0) {
    cutIndex = maxLength;
  }

  const title = text.slice(0, cutIndex).trim() + ' ...';
  const content = '... ' + text.slice(cutIndex).trim();

  return { title, content };
};

export const calculateServicePricesWithCurrency = (
  prices: {
    applicationClassPrices: IApplicationClassPrice[];
    searchClassPrices: IApplicationClassPrice[];
    finalFees?: IApplicationClassPrice[];
  },
  conversionRate: number,
  isoCode: string,
  locale: string
) => {
  const { applicationClassPrices, searchClassPrices } = prices;
  // Find first price with value different than 0 starting at index = 1 (the second one) for applicationClassPrices, searchClassPrices and finalFees
  const additionalRegistrationClassPrice = applicationClassPrices
    .slice(1)
    .find((x) => x.value > 0);
  const additionalSearchClassPrice = searchClassPrices
    .slice(1)
    .find((x) => x.value > 0);
  const additionalFinalFeesPrice = prices.finalFees
    ?.slice(1)
    .find((x) => x.value > 0);

  return {
    currencyApplicationClassPrice: !!applicationClassPrices[0]
      ? addCurrencySymbol(
          applyCurrencyConversionRate(
            applicationClassPrices[0].value,
            conversionRate
          ),
          isoCode,
          locale
        )
      : '-',
    currencyApplicationAdditionalClassPrice: !!additionalRegistrationClassPrice
      ? addCurrencySymbol(
          applyCurrencyConversionRate(
            additionalRegistrationClassPrice.value,
            conversionRate
          ),
          isoCode,
          locale
        )
      : '-',
    currencySearchClassPrice: !!searchClassPrices[0]
      ? addCurrencySymbol(
          applyCurrencyConversionRate(
            searchClassPrices[0].value,
            conversionRate
          ),
          isoCode,
          locale
        )
      : '-',
    currencySearchAdditionalClassPrice: !!additionalSearchClassPrice
      ? addCurrencySymbol(
          applyCurrencyConversionRate(
            additionalSearchClassPrice.value,
            conversionRate
          ),
          isoCode,
          locale
        )
      : '-',
    finalFeesFirstClass:
      prices.finalFees && !!prices.finalFees[0]
        ? addCurrencySymbol(
            applyCurrencyConversionRate(
              prices.finalFees[0].value,
              conversionRate
            ),
            isoCode,
            locale
          )
        : '-',
    finalFeesAdditionalClass: !!additionalFinalFeesPrice
      ? addCurrencySymbol(
          applyCurrencyConversionRate(
            additionalFinalFeesPrice.value,
            conversionRate
          ),
          isoCode,
          locale
        )
      : '-',
  };
};

export const localeFullCode = (locale: string) => {
  switch (locale) {
    case 'es':
      return 'es-ES';
    case 'en':
      return 'en-US';
    case 'fr':
      return 'fr-FR';
    default:
      return 'en-US';
  }
};
